import { Injectable, inject } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { DashboardsService } from "@shared/dashboards-service";
import type {
    GetCardResponse,
    UpdateCardResponse,
    PatchCardBatchResponse,
    DeleteCardResponse,
    SearchCardsResponse,
    IBaseCard,
} from "@app/views-interfaces";
import { HttpService } from "@classes";
import { CardUpdateRequest } from "@app/views-interfaces/card-update-request";

// Use to build the 'cards' object for bulk change
type CardBatchTypes = Record<string, IBaseCard>;

export class ICardServiceResponse {
    Code: number;
    ContToken: string;
    Cards: IBaseCard[];
}

@Injectable({ providedIn: "root" })
export class CardService extends HttpService {
    private readonly dashboardsService = inject(DashboardsService);

    protected path = "cards/";
    protected urlKey = "CardsService_URL";
    protected versionKey = "CardsService_Version";

    protected getUrl(path = this.path, dashboardId = this.dashboardsService.getCurrentDashboardIdOrThrow()): string {
        return super.getUrl(path) + dashboardId + "/";
    }

    public getCardById(cardId: string, dashboardId = this.dashboardsService.getCurrentDashboardIdOrThrow()) {
        return this.get<GetCardResponse>(encodeURIComponent(cardId), {
            baseUrl: this.getUrl(this.path, dashboardId),
        });
    }

    public getCardsFromServer(top = "99999999", select = "", maxPageSize = "99999999", contToken = undefined) {
        let headers = new HttpHeaders();
        if (maxPageSize) headers = headers.set("Prefer", "odata.maxpagesize=" + maxPageSize);
        if (contToken) headers = headers.set("OData", "odata.nextLink=" + contToken);
        return this.get<ICardServiceResponse>("?$top=" + top + select, { headers });
    }

    updateCard(cardId: string, cardData: { card: CardUpdateRequest }) {
        const card = Object.fromEntries(Object.entries(cardData.card).map(([key, value]) => [key, value === "null" ? null : value]));
        return this.put<UpdateCardResponse>(cardId, { card });
    }

    public patchCardBatch(cardBatch) {
        const cardsToSend: CardBatchTypes = {};
        const cardIds = Object.keys(cardBatch);
        cardIds.forEach((cardId) => {
            const card = cardBatch[cardId];
            const properties = Object.keys(card);
            properties.forEach((prop) => {
                if (card[prop] === "null") {
                    card[prop] = null;
                }
            });
            // Format the object to send to the service
            cardsToSend[cardId.toString()] = card;
        });

        return this.patch<PatchCardBatchResponse>("", { cards: cardsToSend });
    }

    public deleteCard(cardId: string) {
        return this.delete<DeleteCardResponse>(cardId);
    }

    public searchCardsByTerm(filterString: string) {
        return this.get<SearchCardsResponse>("?$filter=" + filterString + "&$top=50");
    }
}
